import React, { useState, useCallback } from "react";

import {
  Box,
  Paper,
  Button,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import RestoreIcon from "@mui/icons-material/Restore";

import fileSaver from "file-saver";

import { FormatDateTime } from "../../../utils/formateDate";

function ReportItemDashboard({
  report: propReport,
  typeActivite,
  nomEmplacement,
  username,
  date,
  dateEvent,
  setReloadReports,
}) {
  const { t } = useTranslation(["Articles", "Common"]);

  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [emailLoading, setEmailLoading] = useState(false);

  // Confirmation dialog
  const [openEmailConfirmation, setOpenEmailConfirmation] = useState(false);

  const userState = useSelector((state) => state.user);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isValidReport =
    !!propReport?.idActivite && !!propReport?.idTypeActivite;

  // =========================================================
  // ROLES
  // =========================================================

  const hasEmailRole = ["AdminTI", "ReportAdmin", "ReportWriter"].some((role) =>
    userState.roles?.includes(role),
  );

  // =========================================================
  // FORMAT EMAIL SENT DATE
  // =========================================================

  const formatEmailSentDate = useCallback((value) => {
    if (!value) return null;

    const emailDate = new Date(value);

    if (Number.isNaN(emailDate.getTime())) {
      return null;
    }

    const year = emailDate.getFullYear();
    const month = String(emailDate.getMonth() + 1).padStart(2, "0");
    const day = String(emailDate.getDate()).padStart(2, "0");

    const hours = String(emailDate.getHours()).padStart(2, "0");
    const minutes = String(emailDate.getMinutes()).padStart(2, "0");

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }, []);

  // =========================================================
  // EMAIL SENT DATE
  // =========================================================
  //
  // IMPORTANT:
  // The backend/API must eventually provide:
  //
  // propReport.emailFirstSentDate
  //
  // Example:
  // "2026-09-14T14:56:00"
  //
  // =========================================================

  const emailFirstSentDate = formatEmailSentDate(
    propReport?.emailFirstSentDate,
  );

  // =========================================================
  // EMAIL TOOLTIP
  // =========================================================

  const emailTooltip = propReport?.alreadySent
    ? emailFirstSentDate
      ? userState.lang === "FR"
        ? `Courriel envoyé pour la première fois le ${emailFirstSentDate}`
        : `Email first sent on ${emailFirstSentDate}`
      : userState.lang === "FR"
        ? "Courriel déjà envoyé"
        : "Email already sent"
    : userState.lang === "FR"
      ? "Envoyer par courriel"
      : "Email Report";

  // =========================================================
  // DOWNLOAD REPORT
  // =========================================================

  const handleDownloadClick = useCallback(async () => {
    if (!isValidReport) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/DownloadReportPdf/${propReport.idActivite}/${propReport.idTypeActivite}/${userState.lang}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléchargement");
      }

      const blob = await response.blob();

      fileSaver.saveAs(
        blob,
        `${propReport.idActivite}_${
          userState.lang === "EN"
            ? propReport.titleRepportEn
            : propReport.titleRepportFr
        }.pdf`,
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isValidReport, propReport, userState.lang, userState.token]);

  // =========================================================
  // REOPEN REPORT
  // =========================================================

  const handleReopen = useCallback(async () => {
    if (!isValidReport) return;

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/Report/ReopenReport`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify({
          id: propReport.idActivite,
        }),
      });

      if (setReloadReports) {
        setReloadReports(true);
      }

      setOpenSnackbar(true);
    } catch (err) {
      console.error(err);
    }
  }, [isValidReport, propReport, setReloadReports, userState.token]);

  // =========================================================
  // OPEN EMAIL CONFIRMATION
  // =========================================================

  const handleEmailClick = useCallback(() => {
    if (!isValidReport) return;

    setOpenEmailConfirmation(true);
  }, [isValidReport]);

  // =========================================================
  // CLOSE EMAIL CONFIRMATION
  // =========================================================

  const handleCancelEmail = useCallback(() => {
    if (emailLoading) return;

    setOpenEmailConfirmation(false);
  }, [emailLoading]);

  // =========================================================
  // SEND EMAIL
  // =========================================================

  const handleEmailReport = useCallback(async () => {
    if (!isValidReport) return;

    try {
      setEmailLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/EmailReport/${propReport.idActivite}/${userState.lang}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi du courriel");
      }

      // Close confirmation after successful send
      setOpenEmailConfirmation(false);

      // Reload reports so that alreadySent/date can be refreshed
      if (setReloadReports) {
        await setReloadReports();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setEmailLoading(false);
    }
  }, [
    isValidReport,
    propReport,
    userState.lang,
    userState.token,
    setReloadReports,
  ]);

  // =========================================================
  // DEBUG
  // =========================================================

  console.log("ROLES:", userState.roles);
  console.log("emailAllowed:", propReport?.emailAllowed);
  console.log("alreadySent:", propReport?.alreadySent);
  console.log("emailFirstSentDate:", propReport?.emailFirstSentDate);
  console.log("formattedEmailFirstSentDate:", emailFirstSentDate);
  console.log("hasEmailRole:", hasEmailRole);

  // =========================================================
  // UI
  // =========================================================

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 1,
          borderRadius: 2,
          backgroundColor: "#f9f9f9",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 1,
          }}
        >
          {/* =====================================================
              REPORT INFORMATION
          ====================================================== */}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {typeActivite}
            </Typography>

            <Typography variant="body2">{nomEmplacement}</Typography>

            <Typography variant="body2" fontWeight="bold">
              {username}
            </Typography>

            <Typography variant="caption">
              {FormatDateTime(dateEvent)}
            </Typography>

            <Typography variant="caption">{FormatDateTime(date)}</Typography>
          </Box>

          {/* =====================================================
              ACTIONS
          ====================================================== */}

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mt: isMobile ? 1 : 0,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* ===================================================
                REOPEN
            ==================================================== */}

            {userState.roles?.some(
              (role) => role === "AdminTI" || role === "ReportAdmin",
            ) && (
              <Tooltip title={t("Reopen")}>
                <span>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleReopen}
                    disabled={!isValidReport}
                    size="small"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      p: 0,
                    }}
                  >
                    <RestoreIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* ===================================================
                EMAIL
            ==================================================== */}

            {propReport?.emailAllowed && hasEmailRole && (
              <Tooltip title={emailTooltip}>
                <span>
                  <Button
                    variant="contained"
                    color={propReport?.alreadySent ? "success" : "primary"}
                    onClick={handleEmailClick}
                    disabled={emailLoading || !isValidReport}
                    size="small"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      p: 0,
                    }}
                  >
                    {emailLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <EmailIcon fontSize="small" />
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* ===================================================
                DOWNLOAD PDF
            ==================================================== */}

            <Tooltip title={t("Download")}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleDownloadClick}
                  disabled={loading || !isValidReport}
                  size="small"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    p: 0,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <PictureAsPdfIcon fontSize="small" />
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* =========================================================
          REOPEN SNACKBAR
      ========================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("Report reopened successfully!")}
        </Alert>
      </Snackbar>

      {/* =========================================================
          EMAIL CONFIRMATION DIALOG
      ========================================================== */}

      <Dialog
        open={openEmailConfirmation}
        onClose={handleCancelEmail}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.18)",
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            p: 0,
            background: (theme) => theme.palette.primary.main,
            color: "white",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.16)",
                flexShrink: 0,
              }}
            >
              <EmailIcon sx={{ fontSize: 26 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                {userState.lang === "FR"
                  ? "Confirmation d'envoi"
                  : "Send confirmation"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                {userState.lang === "FR"
                  ? "Vérifiez les informations avant l'envoi"
                  : "Review the information before sending"}
              </Typography>
            </Box>

            <Button
              onClick={handleCancelEmail}
              disabled={emailLoading}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: "50%",
                color: "white",
                p: 0,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={2.5}>
            {/* Message */}
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ lineHeight: 1.7 }}
            >
              {userState.lang === "FR"
                ? "Vous êtes sur le point d'envoyer ce rapport par courriel."
                : "You are about to send this report by email."}
            </Typography>

            {/* REPORT CARD */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.grey[50],
              }}
            >
              <Stack spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  textTransform="uppercase"
                >
                  {userState.lang === "FR" ? "Rapport" : "Report"}
                </Typography>

                <Typography
                  variant="body1"
                  fontWeight={700}
                  color="text.primary"
                >
                  {userState.lang === "FR"
                    ? propReport?.titleRepportFr
                    : propReport?.titleRepportEn}
                </Typography>

                {nomEmplacement && (
                  <Typography variant="body2" color="text.secondary">
                    {nomEmplacement}
                  </Typography>
                )}
              </Stack>
            </Paper>

            {/* WARNING / INFO */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
                p: 2,
                borderRadius: 2,
                backgroundColor: "rgba(25, 118, 210, 0.06)",
                border: "1px solid",
                borderColor: "rgba(25, 118, 210, 0.15)",
              }}
            >
              <EmailIcon color="primary" sx={{ mt: 0.2 }} />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                {userState.lang === "FR"
                  ? "Le rapport sera envoyé par courriel aux destinataires configurés pour cette activité."
                  : "The report will be sent by email to the recipients configured for this activity."}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <Divider />

        {/* ACTIONS */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleCancelEmail}
            disabled={emailLoading}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 110,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {userState.lang === "FR" ? "Annuler" : "Cancel"}
          </Button>

          <Button
            onClick={handleEmailReport}
            disabled={emailLoading}
            variant="contained"
            color="primary"
            startIcon={
              emailLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <EmailIcon />
              )
            }
            sx={{
              minWidth: 130,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
          >
            {emailLoading
              ? userState.lang === "FR"
                ? "Envoi..."
                : "Sending..."
              : userState.lang === "FR"
                ? "Envoyer"
                : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default React.memo(ReportItemDashboard);
